import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { Link, useParams, useNavigate } from "react-router-dom";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* =============== helpers: LOCAL Y-M-D (avoid tz issues) =============== */

const isBlank = (v) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

const isNegativeValue = (v) => {
  if (typeof v === "number") return v < 0;
  const s = String(v).trim();
  if (s === "") return false;
  return s.startsWith("-");
};

const isNonNegativeIntegerValue = (v) => {
  if (typeof v === "number") return Number.isInteger(v) && v >= 0;
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return false;
  const n = Number(s);
  return Number.isInteger(n) && n >= 0;
};

const focusField = (id) => document.getElementById(id)?.focus();



const pad2 = (n) => String(n).padStart(2, "0");
const ymdLocal = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDaysLocal = (yyyy_mm_dd, n) => {
  if (!yyyy_mm_dd) return "";
  const d = new Date(`${yyyy_mm_dd}T00:00:00`);
  d.setDate(d.getDate() + n);
  return ymdLocal(d);
};

// format date as d/m/y (no time)
const formatDMY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};


const to24h = (h12, ampm) =>
  ampm === "AM" ? Number(h12) % 12 : (Number(h12) % 12) + 12;

const minutesFromDateHM = (dateStr, h24, m) => {
  if (!dateStr) return NaN;
  const y = Number(dateStr.slice(0, 4));
  const mo = Number(dateStr.slice(5, 7));
  const d = Number(dateStr.slice(8, 10));
  const dayOrd = y * 372 + mo * 31 + d; // monotonic ordinal (no TZ math)
  return dayOrd * 1440 + h24 * 60 + m;
};

const decFromHM = (h24, m) => +(h24 + m / 60).toFixed(2);
const hhmm24 = (h24, m) => `${pad2(h24)}:${pad2(m)}`;

const isNonNegativeNumber = (v) => v !== "" && !isNaN(Number(v)) && Number(v) >= 0;
const isNonNegativeInt = (v) => v !== "" && /^\d+$/.test(String(v));

const Vehicle_position_entry_hire = () => {
  useAuthRedirect();
  const { vcode, branch, vehname, vehno, vtype, basis } = useParams(); // basis: "Hour" | "Km" | "Kmhour" | "Ton" | "Month"
  const navigate = useNavigate();
  const canEntry = hasPermission("Vehicle Daily Progress Entry", "entry");

  const [locations, setLocations] = useState([]);
  const [hireLocations, setHireLocations] = useState([]);
  const [uom, setUom] = useState([]);

  const [formData, setFormData] = useState({
    driver: "",
    sthrs: "", // for hints only (we'll send computed hour_basis instead)
    endhrs: "",
    stkm: "",
    endkm: "",
    breakhrs: "",
    breakmins: "",
    dqty: "",
    trips: "",
    location: "",
    load: "",   // #loads for Hour/Km/Kmhour
    loads: "",  // #loads for Ton/Month
    remarks: "",
    volume: "",
    expiry: "", // driver licence expiry (Hour only, if you need)
    uom: "",    // for Ton/Month
  });

  /* ====== Hour-basis UI state (dates + 12h) ====== */
  const todayStr = ymdLocal(new Date());
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [startHour12, setStartHour12] = useState("12");
  const [startMinute, setStartMinute] = useState("00");
  const [startAmPm, setStartAmPm] = useState("AM");
  const [endHour12, setEndHour12] = useState("12");
  const [endMinute, setEndMinute] = useState("00");
  const [endAmPm, setEndAmPm] = useState("AM");
  const [endsNextDay, setEndsNextDay] = useState(false);

  /* ====== Load options ====== */
  useEffect(() => {
    axios.get("https://rfs2.infraconsoft.net.in/api/locations")
      .then(r => setLocations(r.data.locations || []))
      .catch(err => console.error("Error fetching locations:", err));
    axios.get("https://rfs2.infraconsoft.net.in/api/uom")
      .then(r => setUom(r.data.uom || []))
      .catch(err => console.error("Error fetching uom:", err));
  }, []);

  useEffect(() => {
    if (!branch) return;
    axios.get(`https://rfs2.infraconsoft.net.in/api/locations_hire?branch_code=${branch}`)
      .then(r => setHireLocations(r.data.locations || []))
      .catch(err => console.error("Error fetching hire locations:", err));
  }, [branch]);

  /* ====== computed hour math ====== */
  const hStart24 = to24h(startHour12, startAmPm);
  const mStart = Number(startMinute);
  const hEnd24 = to24h(endHour12, endAmPm);
  const mEnd = Number(endMinute);

  const startMin = minutesFromDateHM(startDate, hStart24, mStart);
  const endMin = minutesFromDateHM(endDate, hEnd24, mEnd);
  const durMin = (isNaN(startMin) || isNaN(endMin)) ? 0 : Math.max(0, endMin - startMin);
  const durHrs = +(durMin / 60).toFixed(2);
  const startDec = decFromHM(hStart24, mStart);
  const endDec = decFromHM(hEnd24, mEnd);

  /* ====== warnings ====== */
  const previewWarnings = useMemo(() => {
    const msgs = [];
    if (basis === "Hour" && startDate === endDate && endMin < startMin) {
      msgs.push("End time is earlier than start time on the same day. Did you mean “Ends next day”?");
    }
    if (basis === "Hour" && durMin === 0) {
      msgs.push("Duration is 0 minutes. If this is an overnight (e.g., 12:00 AM → next-day 12:00 AM), enable “Ends next day”.");
    }
    return msgs;
  }, [basis, startDate, endDate, startMin, endMin, durMin]);

  /* ====== UI arrays ====== */
  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => pad2(i));

  /* ====== handlers ====== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    const trimmed = ["sthrs","endhrs","stkm","endkm","breakhrs","breakmins","dqty","trips","load","loads","volume"]
      .includes(name) ? value.replace(/\s+/g, "") : value;
    setFormData(prev => ({ ...prev, [name]: trimmed }));
  };

  const handleToggleNextDay = (checked) => {
    setEndsNextDay(checked);
    if (!startDate) return;
    setEndDate(checked ? addDaysLocal(startDate, 1) : startDate);
  };

  const onChangeStartDate = (val) => {
    setStartDate(val);
    if (endsNextDay && val) setEndDate(addDaysLocal(val, 1));
  };

  /* ====== submit ====== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Common numeric validations
    if (formData.dqty !== "" && !isNonNegativeNumber(formData.dqty)) {
      Swal.fire("Validation Error", "Diesel Quantity must be a non-negative number.", "warning"); return;
    }
    if (formData.trips !== "" && !isNonNegativeInt(formData.trips)) {
      Swal.fire("Validation Error", "Trips / Output must be a non-negative whole number.", "warning"); return;
    }
    // loads field: basis Ton/Month uses "loads", others use "load"
    if ((basis === "Ton" || basis === "Month")) {
      if (formData.loads !== "" && !isNonNegativeInt(formData.loads)) {
        Swal.fire("Validation Error", "No of Loads must be a non-negative whole number.", "warning"); return;
      }
    } else {
      if (formData.load !== "" && !isNonNegativeInt(formData.load)) {
        Swal.fire("Validation Error", "No of Loads must be a non-negative whole number.", "warning"); return;
      }
    }
    if (formData.volume !== "" && !isNonNegativeNumber(formData.volume)) {
      Swal.fire("Validation Error", "Volume must be a non-negative number.", "warning"); return;
    }

    let kmWorked = 0;
    let payloadTime = {};

    if (basis === "Hour") {
      if (!startDate || !endDate) {
        Swal.fire("Validation Error", "Start and End dates are required.", "warning"); return;
      }
      if (
        [hStart24, mStart, hEnd24, mEnd].some((x) => Number.isNaN(x)) ||
        hStart24 < 0 || hStart24 > 23 || hEnd24 < 0 || hEnd24 > 23 ||
        mStart < 0 || mStart > 59 || mEnd < 0 || mEnd > 59
      ) {
        Swal.fire("Validation Error", "Please choose valid times.", "warning"); return;
      }
      if (startDate === endDate && endMin < startMin) {
        Swal.fire("Validation Error", "End time is earlier than start time. Toggle “Ends next day”.", "warning"); return;
      }
      if (durMin <= 0) {
        Swal.fire("Validation Error", "Duration is 0. Check your times.", "warning"); return;
      }
      if (durMin > 72 * 60) {
        Swal.fire("Validation Error", "Duration exceeds 72 hours.", "warning"); return;
      }
      

      // ===== Breaking time REQUIRED + numeric validation =====
const hrs = formData.breakhrs;
const mins = formData.breakmins;

if (basis === "Km" || basis === "Kmhour" || basis === "Month" || basis === "Ton") {
/* --- Required checks (one-by-one) --- */
if (isBlank(hrs)) {
  await Swal.fire("Validation Error", "Breaking Hours is required.", "warning");
  focusField("breakhrs");
  return;
}
if (isBlank(mins)) {
  await Swal.fire("Validation Error", "Minutes is required.", "warning");
  focusField("breakmins");
  return;
}

/* --- Hours rules --- */
if (isNegativeValue(hrs)) {
  await Swal.fire("Validation Error", "Breaking Hours cannot be negative.", "warning");
  focusField("breakhrs");
  return;
}
if (!isNonNegativeIntegerValue(hrs)) {
  await Swal.fire("Validation Error", "Breaking Hours must be a whole number (0, 1, 2, ...).", "warning");
  focusField("breakhrs");
  return;
}

/* --- Minutes rules --- */
if (isNegativeValue(mins)) {
  await Swal.fire("Validation Error", "Minutes cannot be negative.", "warning");
  focusField("breakmins");
  return;
}
if (!isNonNegativeIntegerValue(mins)) {
  await Swal.fire("Validation Error", "Minutes must be a whole number (0–59).", "warning");
  focusField("breakmins");
  return;
}
const minsNum = typeof mins === "number" ? mins : Number(mins);
if (!Number.isFinite(minsNum) || minsNum >= 60) {
  await Swal.fire("Validation Error", "Minutes must be less than 60.", "warning");
  focusField("breakmins");
  return;
}
}
// ===== End breaking validation =====


      payloadTime = {
        sthrs: startDec,
        endhrs: endDec,
        work_hrs: durHrs,
        hour_basis: {
          startDate, endDate,
          startHour12, startMinute, startAmPm,
          endHour12, endMinute, endAmPm,
        },
      };
    } else {
      // KM/Kmhour/Ton/Month --> KM validations if stkm/endkm used
      if (basis === "Km" || basis === "Kmhour" || basis === "Month" || basis === "Ton") {
        const startKm = Number(formData.stkm);
        const endKm = Number(formData.endkm);
        if (isNaN(startKm) || isNaN(endKm)) {
          Swal.fire("Validation Error", "Starting and Closing KMs must be numbers.", "warning"); return;
        }
        if (startKm < 0 || endKm < 0) {
          Swal.fire("Validation Error", "Kilometers cannot be negative.", "warning"); return;
        }
        if (endKm < startKm) {
          Swal.fire("Validation Error", "Closing KM cannot be less than Starting KM.", "warning"); return;
        }
        kmWorked = endKm - startKm;
      }

      payloadTime = {
        sthrs: 0,
        endhrs: 0,
        work_hrs: 0,
      };
    }

    const payload = {
      vcode, branch, vehno, vehname, vtype, basis,
      stkm: formData.stkm || null,
      endkm: formData.endkm || null,
      breakhrs: formData.breakhrs || null,
      breakmins: formData.breakmins || null,
      km_worked: kmWorked,
      dqty: formData.dqty || null,
      trips: formData.trips || null,
      tons: formData.tons || null,
      location: formData.location || "",
      load: (basis === "Ton" || basis === "Month") ? null : (formData.load || null),
      loads: (basis === "Ton" || basis === "Month") ? (formData.loads || null) : null,
      remarks: formData.remarks || "",
      volume: formData.volume || null,
      driver: formData.driver || "",
      uom: (basis === "Ton" || basis === "Month") ? (formData.uom || "") : null,
      ...(basis === "Hour" && { expiry: formData.expiry || null }),
      ...payloadTime,
    };

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.post("https://rfs2.infraconsoft.net.in/api/insert_vehicle_position_hire", payload);

      Swal.fire({
        title: "Success!",
        text: "Hire Vehicle position data inserted successfully.",
        icon: "success",
      }).then(() => {
        navigate(
          `/vehicle_working_hours_hire/${encodeURIComponent(basis)}/${encodeURIComponent(vtype)}/${encodeURIComponent(vcode)}/${encodeURIComponent(vehno)}/${encodeURIComponent(vehname)}/${encodeURIComponent(branch)}`
        );
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Insert Failed",
        text: "Submission failed. Please try again.",
      });
    }
  };

  if (!canEntry) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Add Hire Vehicle Position</h2>
            <Link to={`/vehicle_working_hours_hire/${encodeURIComponent(basis)}/${encodeURIComponent(vtype)}/${encodeURIComponent(vcode)}/${encodeURIComponent(vehno)}/${encodeURIComponent(vehname)}/${encodeURIComponent(branch)}`}>
              <p className="back-button"><i className="fa-solid fa-backward"></i>Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="form-container">
          <form className="vehicle-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group"><label>Vehicle Code :</label><input value={vcode} readOnly /></div>
              <div className="form-group"><label>Branch Name :</label><input value={branch} readOnly /></div>
            </div>

            <div className="form-row">
              <div className="form-group"><label>Vehicle Name :</label><input value={vehname} readOnly /></div>
              <div className="form-group"><label>Vehicle Number :</label><input value={vehno} readOnly /></div>
            </div>

            <div className="form-row">
              <div className="form-group"><label>Vehicle Basis :</label><input value={basis} readOnly /></div>
              <div className="form-group"><label>Vehicle Status :</label><input value="Hire" readOnly /></div>
            </div>

            <div className="form-row">
              <div className="form-group"><label>Vehicle Type :</label><input value={vtype} readOnly /></div>
              <div className="form-group"><label></label><input type="hidden" /></div>
            </div>

            <h2 className="center-">Vehicle Position Entry</h2><br></br><br></br>

            {basis === "Hour" && (
              <>

                {previewWarnings.length > 0 && (
  <div className="alert alert--warn" role="status" aria-live="polite">
    <div className="alert__icon" aria-hidden="true">⚠️</div>

    <div className="alert__body">
      {/* header row with title (left) and the toggle (right) */}
      <div className="alert__head">
        <div className="alert__title">Check your time range</div>

        <label className="switch">
          <input
            id="endsNextDay"
            type="checkbox"
            checked={endsNextDay}
            onChange={(e) => handleToggleNextDay(e.target.checked)}
          />
          <span className="switch__track" aria-hidden="true"></span>
          <span className="switch__label">Ends next day</span>
        </label>
      </div>

      {/* message content */}
      <div className="alert__msg">
                      {previewWarnings.map((w, i) => (
                        <div key={i} className="alert__bullet">{w}</div>
                      ))}
        <div className="alert__hint">
          Overnight example: <b>12:00 AM → next-day 12:00 AM</b>. If that’s your case, enable
          <em> Ends next day</em>.
        </div>
      </div>
    </div>
  </div>

  
)}
                <br></br>
                <br></br>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date :</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => onChangeStartDate(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date :</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Time :</label>
                    <div className="time-group field-control">
                      <select value={startHour12} onChange={(e) => setStartHour12(e.target.value)}>
                        {hourOptions.map(h => <option key={h}>{h}</option>)}
                      </select>
                      <span className="sep">:</span>
                      <select value={startMinute} onChange={(e) => setStartMinute(e.target.value)}>
                        {minuteOptions.map(m => <option key={m}>{m}</option>)}
                      </select>
                      <span className="sep"> </span>
                      <select value={startAmPm} onChange={(e) => setStartAmPm(e.target.value)}>
                        <option>AM</option><option>PM</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>End Time :</label>
                    <div className="time-group field-control">
                      <select value={endHour12} onChange={(e) => setEndHour12(e.target.value)}>
                        {hourOptions.map(h => <option key={h}>{h}</option>)}
                      </select>
                      <span className="sep">:</span>
                      <select value={endMinute} onChange={(e) => setEndMinute(e.target.value)}>
                        {minuteOptions.map(m => <option key={m}>{m}</option>)}
                      </select>
                      <span className="sep"> </span>
                      <select value={endAmPm} onChange={(e) => setEndAmPm(e.target.value)}>
                        <option>AM</option><option>PM</option>
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}

            {basis !== "Hour" && (
              <div className="form-row">
                <div className="form-group"><label>Starting km :</label><input type="number" name="stkm" value={formData.stkm} onChange={handleChange} /></div>
                <div className="form-group"><label>Closing km :</label><input type="number" name="endkm" value={formData.endkm} onChange={handleChange} /></div>
              </div>
            )}

            
            {(basis === "Km" || basis === "Kmhour" || basis === "Ton" || basis === "Month") && (
<div className="form-row">
  <div className="form-group">
    <label htmlFor="breakhrs">Breaking Hours :</label>
    <input
      id="breakhrs"
      type="number"
      name="breakhrs"
      min="0"
      step="1"
      value={formData.breakhrs ?? ""}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (["-", "+", ".", "e", "E"].includes(e.key)) e.preventDefault();
      }}
      onWheel={(e) => e.currentTarget.blur()}
    />
  </div>

  <div className="form-group">
    <label htmlFor="breakmins">Minutes :</label>
    <input
      id="breakmins"
      type="number"
      name="breakmins"
      min="0"
      max="59"
      step="1"
      value={formData.breakmins ?? ""}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (["-", "+", ".", "e", "E"].includes(e.key)) e.preventDefault();
      }}
      onWheel={(e) => e.currentTarget.blur()}
    />
  </div>
</div>

            )}

            {basis === "Hour" && (
              <div className="form-row">
                <div className="form-group"><label>Driver Name :</label><input type="text" name="driver" value={formData.driver} onChange={handleChange} /></div>
                <div className="form-group"><label>Driver Licence Expire Date :</label><input type="date" name="expiry" value={formData.expiry} onChange={handleChange} /></div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group"><label>Diesel Quantity :</label><input type="number" step="any" name="dqty" value={formData.dqty} onChange={handleChange} /></div>
              <div className="form-group">
                <label>
  {basis === "Ton" ? "No. of Tons :" : "No. of Trips / Output :"}
</label>
<input
  type="number"
  step="any"
  name={basis === "Ton" ? "tons" : "trips"}
  value={basis === "Ton" ? formData.tons : formData.trips}
  onChange={handleChange}
/>
              </div>
            </div>

            <div className="form-row">
              {(basis === "Hour") ? (
                <div className="form-group">
                  <label>Location :</label>
                  <select name="location" value={formData.location} onChange={handleChange}>
                    <option value="">-- Select Location --</option>
                    {locations.map((l, i) => <option key={i} value={l.fld_location}>{l.fld_location}</option>)}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Location :</label>
                  <select name="location" value={formData.location} onChange={handleChange}>
                    <option value="">-- Select Location --</option>
                    {hireLocations.map((l, i) => <option key={i} value={l.fld_location}>{l.fld_location}</option>)}
                  </select>
                </div>
              )}

              {(basis === "Ton" || basis === "Month") ? (
                <div className="form-group"><label>No of Load :</label><input type="number" name="loads" value={formData.loads} onChange={handleChange} /></div>
              ) : (
                <div className="form-group"><label>No of Loads :</label><input type="number" name="load" value={formData.load} onChange={handleChange} /></div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group"><label>Remarks :</label><input type="text" name="remarks" value={formData.remarks} onChange={handleChange} /></div>
              <div className="form-group"><label>Volume :</label><input type="number" step="any" name="volume" value={formData.volume} onChange={handleChange} /></div>
            </div>

            {(basis === "Ton" || basis === "Month") && (
              <div className="form-row">
                <div className="form-group">
                  <label>UOM :</label>
                  <select name="uom" value={formData.uom} onChange={handleChange}>
                    <option value="">-- Select UOM --</option>
                    {uom.map((uomm, index) => (
                      <option key={index} value={uomm.uommaster_shortname}>{uomm.uommaster_shortname}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group"><label></label><input type="hidden" /></div>
              </div>
            )}

            <div className="btn-cls">
              <button type="submit" className="submit-btn">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Vehicle_position_entry_hire;
